import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { DacDemandeGroup, DemandeReportsResponse, ReportAdditif } from '@/types';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getReportsDataTableStructure } from './utils/reports-data-table-object';
import { ReportsService } from './reports.service';
import { ReportsRepository } from '@/store/reports-state/reports-state.repository';
import { Router } from '@angular/router';

@Component({
  selector: 'sm-reports',
  standalone: true,
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  imports: [CommonModule, PageHeaderComponent, DataTableBuilderComponent, TranslateModule],
})
export class ReportsComponent implements OnInit {
  reportsService = inject(ReportsService);
  reportsRepository = inject(ReportsRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  breadcrumb = [
    {
      label: 'clarification.reports.plural',
      routerLink: ['/reports'],
    },
  ];

  dataTableObject?: DynamicDataTable;
  loading$ = new BehaviorSubject<boolean>(true);
  reports$ = new BehaviorSubject<DacDemandeGroup[]>([]);

  ngOnInit(): void {
    this.initTable();
    this.loadData();

    this.translate.onLangChange.subscribe(() => {
      this.initTable();
    });
  }

  rowActionsFn = (row: DacDemandeGroup): DataTableRowActions => ({
    defaultActions: {
      inspect: {
        label: 'clarification.actions.consult',
      },
    },
    inspectAction: () => {
      // Find the DAC ID from the first demande or somewhere else. 
      // DacDemandeGroup has dacReference. The DemandeReportItem has dacId.
      const dacId = row.demandes[0]?.dacId;
      if (dacId) {
        this.router.navigate([`/reports/${dacId}`]);
      }
    },
  });

  initTable(): void {
    this.dataTableObject = getReportsDataTableStructure(this.translate);
    this.dataTableObject.createAction = () => {
      this.router.navigate(['/reports/create']);
    };
    this.dataTableObject.rowActionsFn = this.rowActionsFn;
  }

  loadData() {
    this.loading$.next(true);
    this.reportsService.getReportsByAc().subscribe({
      next: (response: DemandeReportsResponse) => {
        this.reports$.next(response.demandes);
        this.reportsRepository.setReports(response.demandes);
        this.loading$.next(false);
      },
      error: error => {
        this.loading$.next(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('clarification.messages.error_summary'),
          detail: error.error?.message || this.translate.instant('clarification.messages.error_loading_tasks'),
        });
      },
    });
  }
}
